import { html, render } from 'lit-html';
import { appStore, actions } from '../store/appStore.js';
import { PackLine } from '../types/index.js';

export class PackLinesSnapshot extends HTMLElement {
  private unsubscribe?: () => void;

  connectedCallback() {
    this.unsubscribe = appStore.subscribe(() => {
      this.render();
    });
    this.render();
  }

  disconnectedCallback() {
    this.unsubscribe?.();
  }

  private renderPackLine = (packLine: PackLine, isExpanded: boolean = false) => {
    const state = appStore.getState();
    const { linkedPackLine } = state;
    // Dynamically determine if this pack line is currently scanning
    const isLinked = packLine.id === linkedPackLine?.id;
    const expandedClasses = isExpanded ? 'flex-1 min-h-0' : '';
    const cardClasses = `pack-line-card ${expandedClasses}`;

    return html`
      <div class="${cardClasses}">
        <div class="mb-4">
          <h3 class="text-3xl font-bold text-gray-900">${packLine.lineLeadName}</h3>
        </div>

        <div class="grid grid-cols-8 gap-4 items-center ${isExpanded ? 'flex-1' : ''}">
          <!-- Line info -->
          <div class="${isExpanded ? 'flex flex-col justify-center' : ''}">
            <div class="font-semibold text-gray-900 text-base ${isExpanded ? 'text-lg' : ''}">Line ${packLine.lineNumber}</div>
            <div class="flex items-center space-x-1 mt-1">
              ${isLinked ? html`
                <span class="w-2 h-2 bg-green-500 rounded-full"></span>
                <span class="text-green-700 font-medium text-xs">Linked</span>
              ` : ''}
            </div>
            <div class="text-xs text-gray-600 mt-1">${packLine.memberCount} Members</div>
          </div>

          <!-- Overall Pace -->
          <div class="bg-blue-50 p-3 rounded-lg text-center col-span-2 ${isExpanded ? 'flex flex-col justify-center' : ''}">
            <div class="text-xs text-blue-600 font-medium mb-1">Overall pace</div>
            <div class="text-3xl font-bold text-blue-700 ${isExpanded ? 'text-5xl lg:text-6xl' : ''}">${packLine.overallPace.toFixed(1)}</div>
            <div class="text-xs text-blue-600">Bags per person/hr</div>
          </div>

          <!-- Current Pace -->
          <div class="bg-green-50 p-3 rounded-lg text-center col-span-2 ${isExpanded ? 'flex flex-col justify-center' : ''}">
            <div class="text-xs text-green-600 font-medium mb-1">Current Pace</div>
            <div class="text-3xl font-bold text-green-700 ${isExpanded ? 'text-5xl lg:text-6xl' : ''}">${packLine.currentPace.toFixed(1)}</div>
            <div class="text-xs text-green-600">Bags per person/hr</div>
          </div>

          <!-- Current Route -->
          <div class="text-center ${isExpanded ? 'flex flex-col justify-center' : ''}">
            <div class="text-xs text-gray-500 font-medium mb-1">Current Route</div>
            <div class="font-semibold text-gray-900 text-base ${isExpanded ? 'text-lg' : ''}">${packLine.currentRoute}</div>
          </div>

          <!-- Bags Packed -->
          <div class="text-center ${isExpanded ? 'flex flex-col justify-center' : ''}">
            <div class="text-xs text-gray-500 font-medium mb-1">Bags Packed</div>
            <div class="font-semibold text-gray-900 text-base ${isExpanded ? 'text-lg' : ''}">${packLine.bagsPacked} / ${packLine.totalBags}</div>
          </div>

          <!-- Completed -->
          <div class="text-center ${isExpanded ? 'flex flex-col justify-center' : ''}">
            <div class="text-xs text-gray-500 font-medium mb-1">Completed</div>
            <div class="font-semibold text-gray-900 text-base ${isExpanded ? 'text-lg' : ''}">${packLine.completion}%</div>
          </div>
        </div>
      </div>
    `;
  };

  private render() {
    const state = appStore.getState();
    const { packLines, isPackLinesExpanded } = state;

    // Hide routes list when expanded
    const routesList = document.querySelector('routes-list') as HTMLElement;
    if (routesList) {
      routesList.style.display = isPackLinesExpanded ? 'none' : 'block';
    }

    if (packLines.length === 0) {
      const template = html`
        <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center text-gray-500 ${isPackLinesExpanded ? 'h-full flex items-center justify-center' : ''}">
          No Active Pack Lines
        </div>
      `;
      render(template, this);
      return;
    }

    const template = html`
      <div class="w-full h-full ${isPackLinesExpanded ? 'flex flex-col' : 'space-y-4'}">
        ${packLines.map((packLine, index) => this.renderPackLine(packLine, isPackLinesExpanded))}
      </div>
    `;

    render(template, this);
  }
}

customElements.define('pack-lines-snapshot', PackLinesSnapshot);
